import { pipe } from 'fp-ts/function';
import { FC, useMemo } from 'react';
import { connect } from 'react-redux';
import { array, record } from 'fp-ts';
import { identity } from 'lodash/fp';
import {
    commanderForm,
    DateTimePickerField,
    formattedCurrency,
    formattedNumber,
    InputField,
    presence,
    SelectField,
    TextareaField,
    validation,
    CurrentEnterpriseCurrencySelectField,
    TaxationSelectFields,
} from '@mews-commander/legacy-form';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { LocalizedText, withAppClockService } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { getCultureCode } from '@mews-commander/core';
import { getNumberCultureInfo } from '@mews-commander/core/legacy-globalization';
import { getCustomersOptions } from '@mews-commander/legacy-entities/src/ducks/customers';
import {
    doesServiceHaveOverridablePrice,
    doesServiceHaveOverridableTax,
    doesServiceOrderRequireCompletedNotes,
    getHasConfigurableBusinessSegment,
} from '@mews-commander/legacy-entities/src/ducks/services';
import { getCurrentEnterpriseTaxEnvironmentCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { serviceToOption } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { useServicesQuery } from '@mews-commander/legacy-entities/src/queries/services/servicesQuery';
import { createOption, FieldsContainer } from '@mews-commander/ui';
import { useEnterpriseBusinessSegmentsQuery } from '@mews-commander/core/entities';
import * as fieldNames from '../../constants';
import { ConstraintsProps, DispatchProps, OwnProps, ServiceFormProps, StateProps } from '../../types';
import {
    initializeProductServiceOrderForm,
    loadServiceDataForProductServiceOrderForm,
} from '../ProductServiceOrderCreatePage/initialize';
import { getProductListInputInitialValues, getServiceOrderFormInitialValues } from '../../selectors';

const CreateServiceForm: FC<ServiceFormProps> = ({
    form,
    serviceId,
    taxEnvironmentCode,
    hasOverridablePrice,
    hasOverridableTax,
    hasConfigurableBusinessSegment,
    requiresNonEmptyNotes,
    accountOptions,
    serviceDisabled,
    customerDisabled,
    onlyOfferedToEmployee,
}) => {
    const { data: businessSegmentsOptions } = useEnterpriseBusinessSegmentsQuery(({ businessSegments }) =>
        businessSegments
            .filter((segment) => segment.serviceId === serviceId)
            .map(({ id, name }) => createOption({ value: id, label: name }))
    );
    const servicesQuery = useServicesQuery();
    const productServiceOptions = useMemo(
        () =>
            servicesQuery.data
                ? pipe(
                      servicesQuery.data.aggregatedData.additionalServices,
                      onlyOfferedToEmployee ? array.filter((service) => service.options.offerToEmployee) : identity,
                      array.map(serviceToOption)
                  )
                : [],
        [servicesQuery, onlyOfferedToEmployee]
    );
    return (
        <FieldsContainer>
            <SelectField
                label={<LocalizedText textKey={TextKey.Service} />}
                name={fieldNames.SERVICE_ID}
                options={productServiceOptions}
                disabled={serviceDisabled}
                required
            />

            <SelectField
                label={<LocalizedText textKey={TextKey.Customer} />}
                name={fieldNames.CUSTOMER_ID}
                options={accountOptions}
                disabled={customerDisabled}
                required
            />

            <DateTimePickerField label={<LocalizedText textKey={TextKey.Date} />} form={form} />

            {hasConfigurableBusinessSegment && (
                <SelectField
                    label={<LocalizedText textKey={TextKey.Segment} />}
                    name={fieldNames.SEGMENT}
                    options={businessSegmentsOptions}
                    showEmptyValue
                />
            )}

            {hasOverridablePrice && (
                <CurrentEnterpriseCurrencySelectField
                    label={<LocalizedText textKey={TextKey.Currency} />}
                    name={fieldNames.CURRENCY}
                    required
                />
            )}

            {hasOverridablePrice && (
                <InputField label={<LocalizedText textKey={TextKey.Amount} />} name={fieldNames.AMOUNT} />
            )}

            {hasOverridableTax && (
                <TaxationSelectFields
                    taxEnvironmentCode={taxEnvironmentCode}
                    label={<LocalizedText textKey={TextKey.TaxRate} />}
                    name={fieldNames.TAX_RATE}
                    showEmptyValue
                />
            )}

            <TextareaField
                label={<LocalizedText textKey={TextKey.Notes} />}
                required={requiresNonEmptyNotes}
                name={fieldNames.NOTES}
            />
        </FieldsContainer>
    );
};

const getConstraints = ({ requiresNonEmptyNotes, initialProductIds }: ConstraintsProps) => {
    const dynamicFieldConstrains = pipe(
        initialProductIds,
        array.map((id) => `${fieldNames.UNITS}.${id}.${fieldNames.UNIT_COST}`),
        array.reduce({}, (acc, id) => record.upsertAt(id, { formattedCurrency })(acc))
    );

    return {
        [fieldNames.AMOUNT]: {
            presence,
            formattedNumber,
        },
        [fieldNames.NOTES]: requiresNonEmptyNotes ? { presence } : {},
        [fieldNames.CUSTOMER_ID]: { presence },
        [fieldNames.SERVICE_ID]: { presence },
        ...dynamicFieldConstrains,
    };
};

export default pipe(
    CreateServiceForm,
    commanderForm({
        submitLabel: <LocalizedText textKey={TextKey.Create} />,
        submitButtonProps: { variant: 'primary' },
        validate: validation(getConstraints),
    }),
    connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
        (state, { serviceId, customerId, guestIds, appClockService }) => ({
            initialValues: getServiceOrderFormInitialValues(state, { serviceId, customerId, appClockService }),
            requiresNonEmptyNotes: doesServiceOrderRequireCompletedNotes(state, { id: serviceId }),
            initialProductIds: pipe(getProductListInputInitialValues(state, { serviceId }), record.keys),
            hasConfigurableBusinessSegment: getHasConfigurableBusinessSegment(state, { id: serviceId }),
            hasOverridablePrice: doesServiceHaveOverridablePrice(state, { id: serviceId }),
            hasOverridableTax: doesServiceHaveOverridableTax(state, { id: serviceId }),
            taxEnvironmentCode: getCurrentEnterpriseTaxEnvironmentCode(state),
            numberLocalizationInfo: getNumberCultureInfo(state, { cultureCode: getCultureCode() }),
            accountOptions: getCustomersOptions(state, [customerId, ...(guestIds ?? [])]),
        }),
        (dispatch: CommanderThunkDispatch, { customerId }) => ({
            onChange: (values, _dispatch, props, previousValues) => {
                const oldServiceId = previousValues[fieldNames.SERVICE_ID];
                const newServiceId = values[fieldNames.SERVICE_ID];

                if (newServiceId && newServiceId !== oldServiceId) {
                    _dispatch(loadServiceDataForProductServiceOrderForm(newServiceId));
                    _dispatch(initializeProductServiceOrderForm(newServiceId, values.customerId ?? customerId));
                }
            },
        })
    ),
    withAppClockService
);
